import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { helpScreenForPath } from '@alexiarest/shared';
import { portadaDe, sinPnl, soloCosteo } from '../auth/roles';
import { useAuth } from '../auth/AuthContext';
import { isDemoMode } from '../api/client';
import { useMarkTourSeen, useMe } from '../api/hooks';
import { activeCurrency, activeLocale, configureLocale, configureMoney } from '../format';
import { HelpPanel } from '../help/HelpPanel';
import type { TourHandle } from '../help/tour';
import { PeriodPicker } from './PeriodPicker';
import { LocationPicker } from './LocationPicker';
import { SupportButton } from './SupportButton';
import { TrialPill } from './TrialPill';
import { ThemeToggle } from './ThemeToggle';

/** En la demo el tour visto se recuerda en el navegador: no hay servidor que lo guarde. */
const CLAVE_TOUR_DEMO = 'alexia:tour-visto';


interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const GENERAL: NavItem[] = [
  { to: '/', label: 'Panel general', end: true },
  { to: '/asistente', label: 'Alexia, tu asistente' },
];

const COSTEO: NavItem[] = [
  { to: '/ingredientes', label: 'Ingredientes' },
  { to: '/inventario', label: 'Inventario' },
  { to: '/proveedores', label: 'Proveedores' },
  { to: '/recetas', label: 'Recetas y costeo' },
  { to: '/precios', label: 'Precios de la carta' },
];

// Tesoreria queda fuera del menu en el MVP (decision de la reunion con Alex:
// demasiado trabajo manual por ahora). La ruta, el motor y la API siguen
// vivos: volvera al menu cuando el importador de datos le quite la friccion.
const FINANCIERO: NavItem[] = [
  { to: '/ingresos', label: 'Ingresos' },
  { to: '/gastos', label: 'Gastos' },
  { to: '/analisis', label: 'Análisis' },
  { to: '/resultados', label: 'Cuenta de resultados' },
  { to: '/inversiones', label: 'Inversiones' },
  { to: '/objetivos', label: 'Objetivos' },
  { to: '/impuestos', label: 'Impuestos' },
];

/**
 * El encargado (`staff`) no ve la P&L (decision de Pol y Alex, 21 ago): fuera
 * del menu la portada, Analisis, Resultados, Objetivos e Impuestos. Cocina
 * (`kitchen`, 7 sep) solo ve la seccion Costeo. La API los veta igualmente
 * (403); esto solo evita ensenyar puertas cerradas. Ver auth/roles.ts.
 */
const SIN_PNL = ['/', '/asistente', '/analisis', '/resultados', '/objetivos', '/impuestos'];

function itemsForRole(items: NavItem[], role?: string): NavItem[] {
  if (soloCosteo(role)) return [];
  if (!sinPnl(role)) return items;
  return items.filter((item) => !SIN_PNL.includes(item.to));
}

function NavSection({ title, items }: { title: string; items: NavItem[] }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="nav__section">
      <p className="nav__title">{title}</p>
      <ul className="nav__list">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav__link ${isActive ? 'is-active' : ''}`.trim()}
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Layout(): JSX.Element {
  const { user, logout, isDemo } = useAuth();
  const location = useLocation();

  /*
   * Multi-mercado: la moneda del tenant manda en todos los importes. Cuando
   * llega (o cambia en Ajustes) se reconfiguran los formateadores y se repinta
   * el contenido para que ninguna cifra quede en la moneda vieja.
   *
   * La diferencia importante es REPINTAR vs REMONTAR. Al arrancar, la moneda
   * llega despues del primer render, y remontar ahi le borra a la pantalla
   * todo lo que hubiera leido al montarse. Se veia en cualquier restaurante
   * que no fuera del euro: la vuelta de Stripe (/ajustes?pago=ok) aterrizaba
   * en la pestanya equivocada porque Ajustes se montaba dos veces y la segunda
   * ya no tenia el parametro. Asi que la primera vez solo se repinta.
   *
   * Un cambio POSTERIOR (en Ajustes) si remonta: ahi ya hay cifras pintadas y
   * valores memorizados que no se enterarian de otra forma.
   */
  const me = useMe();
  // Este estado solo existe para repintar; la clave del remontaje va aparte.
  const [, setMonedaActiva] = useState(activeCurrency());
  const claveContenido = useRef(activeCurrency());
  const primeraMoneda = useRef(true);
  const tenantCurrency = me.data?.tenant?.currency;
  const tenantCountry = me.data?.tenant?.country;
  useEffect(() => {
    if (!tenantCurrency) return;
    // El locale de las cifras va con el pais (mejora 45); se trata igual que
    // la moneda: repintar la primera vez, remontar si cambia en Ajustes.
    const cambioLocale = configureLocale(tenantCountry, tenantCurrency);
    const haCambiado = configureMoney(tenantCurrency) || cambioLocale;
    if (haCambiado && !primeraMoneda.current) claveContenido.current = `${activeCurrency()}-${activeLocale()}`;
    primeraMoneda.current = false;
    if (haCambiado) setMonedaActiva(activeCurrency());
  }, [tenantCurrency, tenantCountry]);

  // En movil la navegacion es un panel que se abre por encima del contenido.
  // Apilarla arriba costaba una pantalla entera de menu antes de ver un solo
  // numero, cada vez que se entraba.
  const [menuAbierto, setMenuAbierto] = useState(false);
  const botonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const cerrar = useCallback(() => setMenuAbierto(false), []);

  // Al navegar se cierra solo: tocar un enlace y que el menu siga tapando lo
  // que acabas de abrir es de las cosas que mas molestan en un movil.
  useEffect(() => {
    setMenuAbierto(false);
  }, [location.pathname]);

  // Escape cierra y devuelve el foco al boton, que es de donde salio.
  useEffect(() => {
    if (!menuAbierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuAbierto(false);
        botonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuAbierto]);

  // Con el panel abierto, el fondo no debe poder desplazarse.
  useEffect(() => {
    if (!menuAbierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [menuAbierto]);

  // Al abrir, el foco entra en el panel para que un lector de pantalla no
  // siga leyendo el contenido de detras.
  useEffect(() => {
    if (menuAbierto) panelRef.current?.focus();
  }, [menuAbierto]);

  /*
   * Ayuda y tour (Alex, 5 sep 2026). El panel sabe en que pantalla esta por
   * la ruta; el tour navega por las pantallas y al cerrarse se marca como
   * visto para este usuario. Se lanza solo la primera vez que alguien entra
   * (tras el cuestionario inicial, o al aceptar una invitacion).
   */
  const navigate = useNavigate();
  /*
   * Altura de la barra superior, publicada como variable CSS: es lo que
   * necesitan las cabeceras fijas de las tablas para pegarse justo debajo
   * (mejora 48). Se mide porque cambia con el selector de periodo.
   */
  const topbarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publicar = () =>
      document.documentElement.style.setProperty('--topbar-alto', `${el.offsetHeight}px`);
    publicar();
    const ro = new ResizeObserver(publicar);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  const pantalla = useMemo(() => helpScreenForPath(location.pathname) ?? null, [location.pathname]);
  const markTourSeen = useMarkTourSeen();
  const marcarVisto = markTourSeen.mutate;
  const role = me.data?.user?.role;
  /*
   * driver.js y su CSS se cargan solo cuando el tour arranca: es codigo que
   * corre una vez por usuario y no tiene que ir en el paquete inicial. El
   * handle se resuelve al cargar; `cancelar` antes de eso solo apaga la
   * bandera y el tour no llega a arrancar.
   */
  const lanzarTour = useCallback((): TourHandle => {
    setAyudaAbierta(false);
    let cancelado = false;
    let handle: TourHandle | null = null;
    void import('../help/tour').then(({ iniciarTour }) => {
      if (cancelado) return;
      handle = iniciarTour({
        navigate,
        role,
        alTerminar: () => {
          if (isDemoMode) {
            try {
              localStorage.setItem(CLAVE_TOUR_DEMO, '1');
            } catch {
              /* sin almacenamiento: se repetira, no pasa nada */
            }
          }
          marcarVisto();
        },
      });
    });
    return {
      cancelar: () => {
        cancelado = true;
        handle?.cancelar();
      },
    };
  }, [navigate, role, marcarVisto]);

  /*
   * Primer acceso: el tour se lanza solo. Va por ref y con dependencias
   * minimas para que un refetch de /me no lo reinicie a mitad, y se cancela
   * (sin marcarlo como visto) si este Layout se desmonta: en desarrollo,
   * StrictMode monta dos veces y el tour del primer montaje intentaba abrir
   * el menu de un componente que ya no existia.
   */
  const lanzarRef = useRef(lanzarTour);
  lanzarRef.current = lanzarTour;
  const cargado = Boolean(me.data);
  const tourSeenAt = me.data?.user?.tourSeenAt;
  /*
   * Solo desde la portada y sin parametros en la URL. Si el usuario aterriza
   * en otro sitio (la vuelta del OAuth del TPV, /ajustes?pago=ok desde
   * Stripe, un enlace directo), el tour NO debe navegar por encima: ya
   * saltara la proxima vez que pase por la portada.
   */
  const portada = portadaDe(role);
  const enPortada = location.pathname === portada && location.search === '';
  /*
   * El tour en marcha vive en un ref y solo se cancela al DESMONTAR el
   * Layout, no cuando cambian las dependencias: el propio tour navega a
   * Ingredientes en el paso 2, `enPortada` pasa a false, y la limpieza del
   * efecto lo cancelaba. Se veia como «1 de 12, Siguiente, y se acaba»
   * (Pol, 6 sep 2026).
   */
  const tourAuto = useRef<TourHandle | null>(null);
  useEffect(() => {
    if (tourAuto.current || !cargado || tourSeenAt || !enPortada) return;
    let vistoEnDemo = false;
    if (isDemoMode) {
      try {
        vistoEnDemo = localStorage.getItem(CLAVE_TOUR_DEMO) === '1';
      } catch {
        vistoEnDemo = false;
      }
    }
    if (vistoEnDemo) return;
    tourAuto.current = lanzarRef.current();
  }, [cargado, tourSeenAt, enPortada]);
  useEffect(
    () => () => {
      tourAuto.current?.cancelar();
      tourAuto.current = null;
    },
    [],
  );

  return (
    <div className={`shell ${menuAbierto ? 'shell--menu-abierto' : ''}`.trim()}>
      <a className="skip-link" href="#contenido">
        Saltar al contenido
      </a>

      {/* Cabecera compacta: solo se ve en movil */}
      <header className="mobile-bar">
        <button
          ref={botonRef}
          type="button"
          className="mobile-bar__menu"
          aria-expanded={menuAbierto}
          aria-controls="navegacion-principal"
          onClick={() => setMenuAbierto((v) => !v)}
        >
          <IconoMenu abierto={menuAbierto} />
          <span className="sr-only">{menuAbierto ? 'Cerrar menú' : 'Abrir menú'}</span>
        </button>
        <img
          className="mobile-bar__logo"
          src="/logo/logotipo-oscuro.svg"
          alt="Alexia Rest"
          width={217}
          height={48}
        />
        <div className="mobile-bar__acciones">
          <TrialPill compacto />
          <BotonAyuda onClick={() => setAyudaAbierta(true)} compacto />
          <ThemeToggle />
        </div>
      </header>

      {menuAbierto && <div className="backdrop" onClick={cerrar} aria-hidden="true" />}

      <aside className="sidebar" id="navegacion-principal" ref={panelRef} tabIndex={-1}>
        <div className="brand">
          {/* Logotipo del sistema de diseno, con el texto ya en curvas: no
              depende de que Bricolage Grotesque haya cargado. */}
          <img
            className="brand__logo"
            src="/logo/logotipo-oscuro.svg"
            alt="Alexia Rest"
            width={217}
            height={48}
          />
          <span className="brand__claim">Costes y rentabilidad</span>
        </div>

        <nav className="nav" aria-label="Navegación principal">
          <NavSection title="General" items={itemsForRole(GENERAL, me.data?.user?.role)} />
          <NavSection title="Costeo" items={COSTEO} />
          <NavSection title="Financiero" items={itemsForRole(FINANCIERO, me.data?.user?.role)} />
          <NavSection
            title="Cuenta"
            items={
              // "Administración" es el panel de la PLATAFORMA: solo aparece
              // para Pol y Alex (y en la demo, para que se pueda enseñar).
              me.data?.user?.platformAdmin
                ? [
                    { to: '/ajustes', label: 'Ajustes' },
                    { to: '/admin', label: 'Administración' },
                  ]
                : [{ to: '/ajustes', label: 'Ajustes' }]
            }
          />
        </nav>

        <div className="sidebar__foot">
          {isDemo && (
            <p className="sidebar__demo">
              Modo demostración: los datos son de ejemplo y no se guardan.
            </p>
          )}
          <p className="sidebar__user">{user?.name ?? 'Invitado'}</p>
          {!isDemoMode && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={logout}>
              Cerrar sesión
            </button>
          )}
        </div>
      </aside>

      <div className="main">
        <header className="topbar" ref={topbarRef}>
          <LocationPicker />
          <PeriodPicker />
          <div className="topbar__actions">
            <TrialPill />
            <BotonAyuda onClick={() => setAyudaAbierta(true)} />
            <ThemeToggle />
          </div>
        </header>
        <main className="content" id="contenido" key={claveContenido.current}>
          <Outlet />
        </main>
      </div>

      {/*
        * Soporte por WhatsApp: flotante abajo a la derecha, como pidio Alex
        * (feedback 3) y aparte de la ayuda: la ayuda son tutoriales y Alexia;
        * aqui contesta una persona. Es la ultima parada del tour.
        */}
      <SupportButton />

      <HelpPanel
        open={ayudaAbierta}
        onClose={() => setAyudaAbierta(false)}
        screen={pantalla}
        role={role}
        onTour={lanzarTour}
      />
    </div>
  );
}

/**
 * Boton «Ayuda» de la cabecera. Va en la barra de escritorio y en la de
 * movil (la primera no se ve en movil); el tour resalta el que este visible.
 */
function BotonAyuda({ onClick, compacto = false }: { onClick: () => void; compacto?: boolean }): JSX.Element {
  return (
    <button
      type="button"
      className={`help-btn ${compacto ? 'help-btn--compacto' : ''}`.trim()}
      onClick={onClick}
      data-tour="ayuda"
      aria-label="Ayuda de esta pantalla"
      title="Ayuda de esta pantalla"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.5v.2" />
        <path d="M12 17h.01" />
      </svg>
      {!compacto && <span>Ayuda</span>}
    </button>
  );
}

function IconoMenu({ abierto }: { abierto: boolean }): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      {abierto ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  );
}
